import React from 'react';
import { WithSectionContextProps } from '../../../Sections/SectionContext';
import Zone from '../../Zone';
import { Props as ZoneProps } from '../Zone';
import AddWidget, { Props as AddWidgetProps } from '../../AddWidgetV2';
import { ModuleType } from '../../../../../types/modules';
import { ZoneType } from '../../../../../types/layout';
import { hashString } from '../../../../utils/hash';
import {
  withMobileContext,
  WithMobileContextProps,
} from '@wix/da-shared-react/pkg/Context/MobileContext';
import s from '../Zone.scss';

export type InstallableModuleInfo = AddWidgetProps['installableModules'];

export interface Props extends WithSectionContextProps {
  className?: string;
  children?: React.ReactNode;
  disableTopAddWidget?: boolean;

  zones: any;
  isCurrentUserProfileOwner: boolean;
  addModule: (zone: ZoneType, moduleType: ModuleType, position: number) => void;
}

export class ZoneGroup extends React.Component<Props & WithMobileContextProps> {
  static defaultProps = {
    disableTopAddWidget: false,
  };

  render() {
    if (!this.props.isCurrentUserProfileOwner) {
      return this.props.children;
    }
    return this.renderChildrenWithAddWidgets();
  }

  /**
   * Takes an array of child components and injects AddWidget components in
   * between every two Zones, and also one at the top and one at the bottom of
   * the ZoneGroup.
   *
   * +-----------------+-----------------+
   * | It turns this:  | Into this:      |
   * +-----------------+-----------------+
   * |                 |                 |
   * | <ZoneGroup>     | <ZoneGroup>     |
   * |                 |   <AddWidget>   |
   * |   <Zone>        |   <Zone>        |
   * |                 |   <AddWidget>   |
   * |   <Zone>        |   <Zone>        |
   * |                 |   <AddWidget>   |
   * |   <Zone>        |   <Zone>        |
   * |   ...           |   ...           |
   * |                 |   <AddWidget>   |
   * |   <Zone>        |   <Zone>        |
   * |                 |   <AddWidget>   |
   * | </ZoneGroup>    | </ZoneGroup>    |
   * |                 |                 |
   * +-----------------+-----------------+
   *
   * Each Zone used to render one AddWidget component above itself, and one
   * below itself. This resulted in having two AddWidgets between every two
   * Zone components. The Zone is now responsible for rendering the AddWidget
   * gizmos between the widgets, and it is now the responsibility of the
   * ZoneGroup to inject the AddWidget gizmos between the Zones. These
   * AddWidget gizmos now allow the user to install the widgets from both the
   * Zone above and the one bellow the AddWidget gizmo.
   *
   * The complexity of this implementation comes from the following requirements:
   *  - ZoneGroup can contain non-Zone components which should break the flow,
   *    and any widgets above should render the bottom AddWidget gizmo, and any
   *    widgets below should render the top AddWidget gizmos
   *  - Zone that is locked behaves like the non-Zone component above
   *  - Zone that is locked but empty, should collapse
   *  - ZoneGroup should skip rendering the top AddWidget if disableTopAddWidget is true
   *  - ZoneGroup must render the top AddWidget if all Zones are empty
   *  - If there is a non-locked Zone above it, installableModules for the
   *    current Zone should show in previous Zone's bottom AddWidget, together
   *    with previous zone's installableModules, bottom AddWidget gizmo should show
   *  - If there are no non-locked Zone's above it, Zone should render both top
   *    and bottom AddWidget
   *  - Empty Zone's should collapse and show its installableModules in
   *    previous or next AddWidget gizmo
   *  - etc.
   *
   * To make things more streamlined and hopefully easier to follow, this is
   * implemented as a series of small array reducers that transform component's
   * children array.  Each reducer does one small thing, which hopefully, will
   * make this process less tedious to understand.
   * First, we take all the children, and transform only Zone components into
   * simple JSON structures that expose some of the Zone metadata that is going
   * to be used by each of the subsequent array reducers (this JSON structure
   * is referred to as the "ZoneData"). We then transform the children array
   * using the metadata from the ZoneData to decide where should we put the
   * AddWidget gizmos, and then in the last step, we finally transform that
   * ZoneData into actual AddWidget and Zone components.
   *
   **/
  renderChildrenWithAddWidgets() {
    const { zones = {} } = this.props;
    let disableTopAddWidget = this.props.disableTopAddWidget;

    return (
      React.Children.toArray(this.props.children)

        // Step 1: Convert Zone components to ZoneData
        .map((child: React.ReactElement<{}>) => {
          // Leave non-Zone components intact
          if (!React.isValidElement(child) || child.type !== Zone) {
            return child;
          }

          const { type, locked } = child.props as ZoneProps;
          const { installedModuleIds = [], installableModuleTypes = [] } =
            zones[type] || {};
          const isZoneEmpty = installedModuleIds.length === 0;

          // Convert the Zone component into a "ZoneData" object that keeps a
          // reference to the actual Zone component and adds some metadata
          // used by the other steps.
          return {
            component: child,
            isZoneData: true,
            isZoneEmpty,
            isZoneLocked: locked,
            installableModulesTop: installableModuleTypes.map(moduleName => ({
              zone: type,
              moduleName,
              position: 0,
            })),
            installableModulesBottom: installableModuleTypes.map(
              moduleName => ({
                zone: type,
                moduleName,
                position: installedModuleIds.length,
              })
            ),
          };
        })

        // Step 2: Remove empty locked Zones
        .reduce((result, child: any) => {
          // ignore everything that is not ZoneData
          if (!child.isZoneData || !child.isZoneLocked) {
            return [...result, child];
          }
          // if Zone is locked and empty, just omit it from the resultset
          if (child.isZoneEmpty) {
            return result;
          }
          // if it's not empty, return the actual component
          return [...result, child.component];
        }, [])

        // Step 3: Try to collapse empty Zones into the previous ZoneData
        .reduce((result, child: any) => {
          // ignore everything that is not ZoneData
          if (!child.isZoneData) {
            return [...result, child];
          }
          const prev = result[result.length - 1];
          // if previous child is not ZoneData, or is locked, we cannot merge, so do nothing
          if (!prev || !prev.isZoneData || prev.isZoneLocked) {
            return [...result, child];
          }
          // If the current zone is empty (has no installed modules)
          // merge its installableModulesTop into previous child's installableModulesBottom
          if (child.isZoneEmpty) {
            prev.installableModulesBottom = [
              ...prev.installableModulesBottom,
              ...child.installableModulesTop,
            ];
            child.installableModulesTop = false;
            // omit this empty zone from the resultset
            return result;
          }
          return [...result, child];
        }, [])

        // Step 4: Try to collapse empty Zones into the next ZoneData
        .reduce((result, child) => {
          // ignore everything that is not ZoneData
          if (!child.isZoneData) {
            return [...result, child];
          }
          const prev = result[result.length - 1];
          // if previous child is not ZoneData, or is not empty, there is nothing to do
          if (!prev || !prev.isZoneData || !prev.isZoneEmpty) {
            return [...result, child];
          }

          // Previous item is empty, move current child to its place, and merge
          // the installableModules
          result[result.length - 1] = {
            ...child,
            installableModulesTop: [
              ...prev.installableModulesBottom,
              ...child.installableModulesTop,
            ],
          };
          return result;
        }, [])

        // Step 5: If there are empty Zones that couldn't be collapsed, make
        // them only show the bottom AddWidget
        .reduce((result, child) => {
          if (child.isZoneData && child.isZoneEmpty) {
            child.installableModulesTop = false;
          }
          return [...result, child];
        }, [])

        // Step 6: Merge top and bottom installableModules between two adjoining Zones
        .reduce((result, child) => {
          // ignore everything that is not ZoneData
          if (!child.isZoneData) {
            return [...result, child];
          }
          const prev = result[result.length - 1];
          // if previous child is not ZoneData, or is locked, we cannot merge, so do nothing
          if (!prev || !prev.isZoneData || prev.isZoneLocked) {
            return [...result, child];
          }

          prev.installableModulesBottom = [
            ...prev.installableModulesBottom,
            ...child.installableModulesTop,
          ];
          child.installableModulesTop = false;
          return [...result, child];
        }, [])

        // Step 7: Remove the top AddWidget if disableTopAddWidget is true
        .reduce((result, child) => {
          // ignore everything that is not ZoneData
          if (!child.isZoneData) {
            return [...result, child];
          }

          if (disableTopAddWidget) {
            child.installableModulesTop = false;
            disableTopAddWidget = false;
          }
          return [...result, child];
        }, [])

        // Step 8: Expand ZoneData back into the actual AddWidget and Zone components
        .reduce((result, child) => {
          // ignore everything that is not ZoneData
          if (!child.isZoneData) {
            return [...result, child];
          }
          return [
            ...result,
            this.renderAddWidget(child.installableModulesTop),
            child.component,
            this.renderAddWidget(child.installableModulesBottom),
          ];
        }, [])
        .filter(x => x)
    );
  }

  renderAddWidget = installableModules => {
    const { isMobile, section } = this.props;
    if (isMobile || !installableModules || !installableModules.length) {
      return null;
    }

    // Make sure there are no duplicates
    installableModules = installableModules.reduce((result, child) => {
      if (result.findIndex(item => item.moduleName === child.moduleName) >= 0) {
        return [...result];
      }
      return [...result, child];
    }, []);

    const uniqueKey = hashString(
      installableModules
        .reduce(
          (result, { zone, moduleName, position }) => [
            ...result,
            `${zone}.${moduleName}.${position}`,
          ],
          []
        )
        .join(':')
    );

    return (
      <AddWidget
        section={section}
        key={`add-widget-${uniqueKey}`}
        className={s['add-widget']}
        addModule={this.props.addModule}
        installableModules={installableModules}
      />
    );
  };
}

export default withMobileContext(ZoneGroup);
